import React from 'react'
import type { TeamOfWeekOut, TOTWPlayerOut } from '../../types'

interface TeamOfWeekProps {
  totw: TeamOfWeekOut | null
  onPlayerClick?: (playerId: number) => void
}

// Position coordinates for common formations on the pitch
const FORMATION_POSITIONS: Record<string, Record<string, { x: number; y: number }>> = {
  '4-3-3': {
    GK: { x: 50, y: 92 },
    LB: { x: 15, y: 70 },
    LCB: { x: 35, y: 75 },
    RCB: { x: 65, y: 75 },
    RB: { x: 85, y: 70 },
    LCM: { x: 25, y: 50 },
    CDM: { x: 50, y: 55 },
    RCM: { x: 75, y: 50 },
    LW: { x: 15, y: 25 },
    ST: { x: 50, y: 18 },
    RW: { x: 85, y: 25 },
  },
  '4-4-2': {
    GK: { x: 50, y: 92 },
    LB: { x: 15, y: 70 },
    LCB: { x: 35, y: 75 },
    RCB: { x: 65, y: 75 },
    RB: { x: 85, y: 70 },
    LM: { x: 15, y: 45 },
    LCM: { x: 35, y: 50 },
    RCM: { x: 65, y: 50 },
    RM: { x: 85, y: 45 },
    LST: { x: 35, y: 20 },
    RST: { x: 65, y: 20 },
  },
  '3-5-2': {
    GK: { x: 50, y: 92 },
    LCB: { x: 25, y: 75 },
    CB: { x: 50, y: 78 },
    RCB: { x: 75, y: 75 },
    LWB: { x: 10, y: 50 },
    LCM: { x: 30, y: 50 },
    CDM: { x: 50, y: 55 },
    RCM: { x: 70, y: 50 },
    RWB: { x: 90, y: 50 },
    LST: { x: 35, y: 20 },
    RST: { x: 65, y: 20 },
  },
}

// Default position by role
const DEFAULT_POSITIONS: Record<string, { x: number; y: number }> = {
  GK: { x: 50, y: 90 },
  DEF: { x: 50, y: 70 },
  MID: { x: 50, y: 50 },
  FWD: { x: 50, y: 25 },
}

// Get position for player based on formation and index
function getPlayerPosition(
  player: TOTWPlayerOut,
  formation: string,
  playersByPosition: Record<string, TOTWPlayerOut[]>
): { x: number; y: number } {
  const formationPositions = FORMATION_POSITIONS[formation]

  // If no specific formation mapping, use defaults
  if (!formationPositions) {
    return DEFAULT_POSITIONS[player.position] || { x: 50, y: 50 }
  }

  // Get players of same position type
  const samePositionPlayers = playersByPosition[player.position] || []
  const playerIndex = samePositionPlayers.findIndex(p => p.player_id === player.player_id)

  // Map based on position and index
  if (player.position === 'GK') {
    return formationPositions.GK || DEFAULT_POSITIONS.GK
  }

  if (player.position === 'DEF') {
    const defKeys = ['LB', 'LCB', 'CB', 'RCB', 'RB', 'LWB', 'RWB'].filter(k => formationPositions[k])
    if (defKeys[playerIndex]) return formationPositions[defKeys[playerIndex]]
    return DEFAULT_POSITIONS.DEF
  }

  if (player.position === 'MID') {
    const midKeys = ['LM', 'LCM', 'CDM', 'CM', 'RCM', 'RM', 'LWB', 'RWB'].filter(k => formationPositions[k])
    if (midKeys[playerIndex]) return formationPositions[midKeys[playerIndex]]
    return DEFAULT_POSITIONS.MID
  }

  if (player.position === 'FWD') {
    const fwdKeys = ['LW', 'ST', 'CF', 'RW', 'LST', 'RST'].filter(k => formationPositions[k])
    if (fwdKeys[playerIndex]) return formationPositions[fwdKeys[playerIndex]]
    return DEFAULT_POSITIONS.FWD
  }

  return { x: 50, y: 50 }
}

// Get rating color
function getRatingColor(rating: number): string {
  if (rating >= 8.5) return 'var(--rating-excellent)'
  if (rating >= 7.5) return 'var(--rating-good)'
  if (rating >= 7.0) return 'var(--rating-average)'
  if (rating >= 6.0) return 'var(--rating-below-average)'
  return 'var(--rating-poor)'
}

export function TeamOfWeek({ totw, onPlayerClick }: TeamOfWeekProps) {
  if (!totw || !totw.players || totw.players.length === 0) {
    return (
      <div className="totw-empty">
        <span>Team of the Week not available</span>
      </div>
    )
  }

  // Group players by position
  const playersByPosition = totw.players.reduce((acc, player) => {
    if (!acc[player.position]) acc[player.position] = []
    acc[player.position].push(player)
    return acc
  }, {} as Record<string, TOTWPlayerOut[]>)

  return (
    <div className="totw-container">
      <div className="totw-header">
        <span className="totw-title">Team of the Week</span>
        {totw.gameweek_name && (
          <span className="totw-gameweek">{totw.gameweek_name}</span>
        )}
      </div>

      <div className="totw-pitch">
        {/* Pitch SVG background */}
        <svg className="totw-pitch-svg" viewBox="0 0 100 110" preserveAspectRatio="xMidYMid meet">
          {/* Pitch outline */}
          <rect x="2" y="5" width="96" height="100" fill="var(--pitch-green)" stroke="var(--pitch-lines)" strokeWidth="0.5" rx="2" />

          {/* Center circle */}
          <circle cx="50" cy="55" r="12" fill="none" stroke="var(--pitch-lines)" strokeWidth="0.3" />
          <circle cx="50" cy="55" r="0.5" fill="var(--pitch-lines)" />

          {/* Center line */}
          <line x1="2" y1="55" x2="98" y2="55" stroke="var(--pitch-lines)" strokeWidth="0.3" />

          {/* Top penalty area */}
          <rect x="22" y="5" width="56" height="18" fill="none" stroke="var(--pitch-lines)" strokeWidth="0.3" />
          <rect x="35" y="5" width="30" height="7" fill="none" stroke="var(--pitch-lines)" strokeWidth="0.3" />
          <circle cx="50" cy="16" r="0.5" fill="var(--pitch-lines)" />

          {/* Bottom penalty area */}
          <rect x="22" y="87" width="56" height="18" fill="none" stroke="var(--pitch-lines)" strokeWidth="0.3" />
          <rect x="35" y="98" width="30" height="7" fill="none" stroke="var(--pitch-lines)" strokeWidth="0.3" />
          <circle cx="50" cy="94" r="0.5" fill="var(--pitch-lines)" />
        </svg>

        {/* Players */}
        <div className="totw-players">
          {totw.players.map((player) => {
            const pos = getPlayerPosition(player, totw.formation, playersByPosition)
            return (
              <button
                key={player.player_id}
                className="totw-player"
                style={{
                  left: `${pos.x}%`,
                  top: `${pos.y}%`,
                }}
                onClick={() => onPlayerClick?.(player.player_id)}
                type="button"
              >
                <div className="totw-player-photo">
                  {player.photo_url ? (
                    <img src={player.photo_url} alt="" />
                  ) : (
                    <div className="totw-player-placeholder">
                      {player.name.charAt(0)}
                    </div>
                  )}
                  <div
                    className="totw-player-rating"
                    style={{ backgroundColor: getRatingColor(player.rating) }}
                  >
                    {player.rating.toFixed(1)}
                  </div>
                </div>
                <div className="totw-player-name">{player.name.split(' ').pop()}</div>
                {(player.goals > 0 || player.assists > 0) && (
                  <div className="totw-player-stats">
                    {player.goals > 0 && <span className="totw-stat-goal">{player.goals}G</span>}
                    {player.assists > 0 && <span className="totw-stat-assist">{player.assists}A</span>}
                  </div>
                )}
              </button>
            )
          })}
        </div>
      </div>

      <div className="totw-formation">
        Formation: {totw.formation}
      </div>
    </div>
  )
}
